import {
  useEffect,
  useState,
} from "react";

import {
  X,
  FolderKanban,
  Loader2,
} from "lucide-react";

import {
  createWorkspace,
} from "../services/workspaceService";

import {
  getEmployees,
} from "../services/employeeService";


const initialForm = {
  workspace_code: "",
  workspace_name: "",
  execution_mode: "Work",
  workspace_lead_employee_uuid: "",
  estimated_hours: "",
  status: "Planning",
  display_order: 1,
  description: "",
};


const CreateWorkspaceDrawer = ({
  open,
  onClose,
  project,
  onWorkspaceCreated,
}) => {

  const [formData, setFormData] =
    useState(initialForm);


  const [employees, setEmployees] =
    useState([]);


  const [employeesLoading, setEmployeesLoading] =
    useState(false);


  const [loading, setLoading] =
    useState(false);


  const [error, setError] =
    useState("");


  // =========================================================
  // PROJECT UUID
  // =========================================================

  const projectUuid =
    project?.project_uuid ||
    project?.uuid ||
    project?.projectUuid ||
    "550e8400-e29b-41d4-a716-446655440000";


  // =========================================================
  // FETCH EMPLOYEES
  // =========================================================

  useEffect(() => {

    if (!open) {
      return;
    }


    const fetchEmployees = async () => {

      try {

        setEmployeesLoading(true);
        setError("");


        const response =
          await getEmployees();


        console.log(
          "EMPLOYEE API RESPONSE:",
          response
        );


        /*
         * Supports:
         *
         * [
         *   {...},
         *   {...}
         * ]
         *
         * OR
         *
         * {
         *   success: true,
         *   data: [...]
         * }
         */

        let employeeList = [];


        if (Array.isArray(response)) {

          employeeList = response;

        } else if (
          Array.isArray(response?.data)
        ) {

          employeeList =
            response.data;

        } else if (
          Array.isArray(
            response?.data?.data
          )
        ) {

          employeeList =
            response.data.data;

        }


        console.log(
          "EMPLOYEE LIST:",
          employeeList
        );


        setEmployees(
          employeeList
        );

      } catch (error) {

        console.error(
          "FETCH EMPLOYEES ERROR:",
          error
        );


        setError(
          error?.response?.data?.message ||
          "Failed to load employees."
        );

      } finally {

        setEmployeesLoading(false);

      }
    };


    fetchEmployees();

  }, [open]);


  // =========================================================
  // GET EMPLOYEE UUID
  // =========================================================

  const getEmployeeUuid = (
    employee
  ) => {

    return (
      employee?.uuid ||
      employee?.employee_uuid ||
      employee?.employeeUuid
    );

  };


  // =========================================================
  // GET EMPLOYEE NAME
  // =========================================================

  const getEmployeeName = (
    employee
  ) => {

    return (
      employee?.name ||
      employee?.employee_name ||
      employee?.full_name ||
      employee?.fullName ||
      "Unnamed Employee"
    );

  };


  // =========================================================
  // GET EMPLOYEE ROLE
  // =========================================================

  const getEmployeeRole = (
    employee
  ) => {

    return (
      employee?.role ||
      employee?.designation ||
      employee?.job_title ||
      ""

    );

  };


  // =========================================================
  // HANDLE CHANGE
  // =========================================================

  const handleChange = (
    e
  ) => {

    const {
      name,
      value,
    } = e.target;


    setFormData(
      (prev) => ({
        ...prev,
        [name]: value,
      })
    );


    if (error) {
      setError("");
    }

  };


  // =========================================================
  // SELECTED EMPLOYEE
  // =========================================================

  const selectedLead =
    employees.find(
      (employee) =>
        String(
          getEmployeeUuid(employee)
        ) ===
        String(
          formData.workspace_lead_employee_uuid
        )
    );


  // =========================================================
  // HANDLE SUBMIT
  // =========================================================

  const handleSubmit = async (
    e
  ) => {

    e.preventDefault();


    setError("");
    setLoading(true);


    // =======================================================
    // VALIDATE PROJECT UUID
    // =======================================================

    if (!projectUuid) {

      setError(
        "Project UUID is missing. Cannot create workspace."
      );

      setLoading(false);

      return;
    }


    // =======================================================
    // VALIDATE TEAM LEAD
    // =======================================================

    if (
      !formData.workspace_lead_employee_uuid
    ) {

      setError(
        "Please select a workspace lead."
      );

      setLoading(false);

      return;
    }


    // =======================================================
    // CREATE PAYLOAD
    // =======================================================

    const workspaceData = {

      workspace_code:
        formData.workspace_code.trim(),

      workspace_name:
        formData.workspace_name.trim(),

      execution_mode:
        formData.execution_mode,

      workspace_lead_employee_uuid:
        formData.workspace_lead_employee_uuid,

      estimated_hours:
        Number(
          formData.estimated_hours
        ),

      status:
        formData.status,

      display_order:
        Number(
          formData.display_order
        ),

      description:
        formData.description.trim(),

    };


    console.log(
      "CREATE WORKSPACE PAYLOAD:",
      workspaceData
    );


    try {

      // =====================================================
      // API
      // =====================================================

      const response =
        await createWorkspace(
          projectUuid,
          workspaceData
        );


      console.log(
        "CREATE WORKSPACE RESPONSE:",
        response
      );


      // =====================================================
      // SEND TO PARENT
      // =====================================================

      onWorkspaceCreated?.(
        response
      );


      // =====================================================
      // RESET
      // =====================================================

      setFormData(
        initialForm
      );


      // =====================================================
      // CLOSE
      // =====================================================

      onClose();

    } catch (error) {

      console.error(
        "CREATE WORKSPACE ERROR:",
        error
      );


      const message =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to create workspace.";


      setError(
        message
      );

    } finally {

      setLoading(false);

    }

  };


  // =========================================================
  // CLOSED
  // =========================================================

  if (!open) {
    return null;
  }


  return (
    <>

      {/* =====================================================
          BACKDROP
      ===================================================== */}

      <div
        className="fixed inset-0 z-[70] bg-black/40"
        onClick={
          loading
            ? undefined
            : onClose
        }
      />


      {/* =====================================================
          DRAWER
      ===================================================== */}

      <div className="fixed right-0 top-0 z-[80] flex h-screen w-full flex-col border-l border-slate-200 bg-white shadow-2xl md:w-[70%] lg:w-1/2">


        {/* ===================================================
            HEADER
        =================================================== */}

        <div className="shrink-0 border-b border-slate-200 px-6 py-5">

          <div className="flex items-center justify-between">

            <div className="flex items-center gap-3">

              <div className="rounded-lg bg-blue-100 p-3">

                <FolderKanban
                  size={22}
                  className="text-blue-600"
                />

              </div>


              <div>

                <h2 className="text-xl font-bold text-slate-800">
                  Create Workspace
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  Add workspace to{" "}
                  {project?.projectName ||
                    project?.project_name ||
                    "this project"}
                </p>

              </div>

            </div>


            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-lg p-2 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            >

              <X size={20} />

            </button>

          </div>

        </div>


        {/* ===================================================
            FORM
        =================================================== */}

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >

          {/* =================================================
              BODY
          ================================================= */}

          <div className="min-h-0 flex-1 overflow-y-auto p-6">

            <div className="space-y-5">


              {/* =================================================
                  ERROR
              ================================================= */}

              {error && (

                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">

                  {error}

                </div>

              )}


              {/* =================================================
                  WORKSPACE CODE
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Workspace Code
                </label>


                <input
                  type="text"
                  name="workspace_code"
                  value={
                    formData.workspace_code
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="WS-006"
                  required
                  disabled={loading}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                />

              </div>


              {/* =================================================
                  WORKSPACE NAME
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Workspace Name
                </label>


                <input
                  type="text"
                  name="workspace_name"
                  value={
                    formData.workspace_name
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Backend Workspace"
                  required
                  disabled={loading}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                />

              </div>


              {/* =================================================
                  EXECUTION MODE
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Execution Mode
                </label>


                <select
                  name="execution_mode"
                  value={
                    formData.execution_mode
                  }
                  onChange={
                    handleChange
                  }
                  disabled={loading}
                  className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                >

                  <option value="Work">
                    Work
                  </option>

                  <option value="Pipeline">
                    Pipeline
                  </option>

                  <option value="Hybrid">
                    Hybrid
                  </option>

                </select>

              </div>


              {/* =================================================
                  WORKSPACE LEAD
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Workspace Lead
                </label>


                <select
                  name="workspace_lead_employee_uuid"
                  value={
                    formData.workspace_lead_employee_uuid
                  }
                  onChange={
                    handleChange
                  }
                  required
                  disabled={
                    loading ||
                    employeesLoading
                  }
                  className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                >

                  <option value="">

                    {employeesLoading
                      ? "Loading employees..."
                      : employees.length === 0
                      ? "No employees available"
                      : "Select workspace lead"}

                  </option>


                  {employees.map(
                    (employee) => {

                      const uuid =
                        getEmployeeUuid(
                          employee
                        );


                      const name =
                        getEmployeeName(
                          employee
                        );


                      const role =
                        getEmployeeRole(
                          employee
                        );


                      return (

                        <option
                          key={uuid}
                          value={uuid}
                        >

                          {name}

                          {role
                            ? ` - ${role}`
                            : ""}

                        </option>

                      );

                    }
                  )}

                </select>


                <p className="mt-2 text-xs text-slate-500">
                  Select an employee who will lead this workspace.
                </p>

              </div>


              {/* =================================================
                  SELECTED TEAM LEAD
              ================================================= */}

              {selectedLead && (

                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">

                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Selected Team Lead
                  </p>


                  <p className="mt-1 font-semibold text-slate-800">

                    {getEmployeeName(
                      selectedLead
                    )}

                  </p>


                  {getEmployeeRole(
                    selectedLead
                  ) && (

                    <p className="mt-1 text-sm text-slate-500">

                      {getEmployeeRole(
                        selectedLead
                      )}

                    </p>

                  )}

                </div>

              )}


              {/* =================================================
                  ESTIMATED HOURS + DISPLAY ORDER
              ================================================= */}

              <div className="grid grid-cols-2 gap-4">

                <div>

                  <label className="text-sm font-medium text-slate-700">
                    Estimated Hours
                  </label>


                  <input
                    type="number"
                    name="estimated_hours"
                    value={
                      formData.estimated_hours
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="250"
                    min="0"
                    required
                    disabled={loading}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                  />

                </div>


                <div>

                  <label className="text-sm font-medium text-slate-700">
                    Display Order
                  </label>


                  <input
                    type="number"
                    name="display_order"
                    value={
                      formData.display_order
                    }
                    onChange={
                      handleChange
                    }
                    min="1"
                    required
                    disabled={loading}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                  />

                </div>

              </div>


              {/* =================================================
                  STATUS
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Status
                </label>


                <select
                  name="status"
                  value={
                    formData.status
                  }
                  onChange={
                    handleChange
                  }
                  disabled={loading}
                  className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                >

                  <option value="Planning">
                    Planning
                  </option>

                  <option value="Active">
                    Active
                  </option>

                  <option value="Completed">
                    Completed
                  </option>

                  <option value="On Hold">
                    On Hold
                  </option>

                </select>

              </div>


              {/* =================================================
                  DESCRIPTION
              ================================================= */}

              <div>

                <label className="text-sm font-medium text-slate-700">
                  Description
                </label>


                <textarea
                  name="description"
                  value={
                    formData.description
                  }
                  onChange={
                    handleChange
                  }
                  rows={5}
                  placeholder="Workspace for backend API development."
                  disabled={loading}
                  className="mt-2 w-full resize-none rounded-lg border border-slate-300 px-4 py-3 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                />

              </div>

            </div>

          </div>


          {/* ===================================================
              FOOTER
          =================================================== */}

          <div className="shrink-0 border-t border-slate-200 bg-white px-6 py-4">

            <div className="flex justify-end gap-3">

              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>


              <button
                type="submit"
                disabled={
                  loading ||
                  employeesLoading ||
                  employees.length === 0
                }
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
              >

                {loading && (

                  <Loader2
                    size={17}
                    className="animate-spin"
                  />

                )}


                {loading
                  ? "Creating..."
                  : "Create Workspace"}

              </button>

            </div>

          </div>

        </form>

      </div>

    </>
  );
};


export default CreateWorkspaceDrawer;